import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import { useClarityTracking } from './hooks/useClarityTracking';
import Layout from './components/Layout';
import ProgressSteps from './components/ProgressSteps';
import LandingScreen from './features/landing/LandingScreen';
import type { UseCaseType } from './features/landing/LandingScreen';
import ExistingAudiences from './features/existingAudience/ExistingAudiences';
import UseCaseFormRenderer from './features/simulationUseCase/UseCaseFormRenderer';
import ReviewAndRun from './features/reviewAndRun/ReviewAndRun';
import SimulationResultsContent from './features/simulationResults/SimulationResultsContent';
import PublicSimulationView from './features/simulationResults/PublicSimulationView';
import ChatPage from './features/chat/ChatPage';
import AdminPanel from './features/admin/AdminPanel';
import AnalysisPage from './features/analysis/AnalysisPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AudienceProvider, useAudience } from './context/AudienceContext';
import { BuildAudienceProvider, useBuildAudience } from './context/BuildAudienceContext';
import { AudienceDataProvider } from './contexts/AudienceContext';
import { SubscriptionProvider } from './context/subscription/SubscriptionContext';
import ErrorBoundary from './components/ErrorBoundary';
import Auth from './features/auth/Auth';
import PasswordReset from './features/auth/PasswordReset';
import ProtectedRoute from './components/ProtectedRoute';
import Button from './components/Button';
import { ArrowLeft } from 'lucide-react';
import ChatWithPersona from './features/chatWithPersonas/ChatWithPersona';
import AskCollectiveLanding from './features/digitalTwin/AskCollectiveLanding';
import AskCollectiveChat from './features/digitalTwin/AskCollectiveChat';
import AskHealthcareChat from './features/digitalTwin/AskHealthcareChat';
import AskNaturalOrganicChat from './features/digitalTwin/AskNaturalOrganicChat';
import SharedChatView from './features/digitalTwin/SharedChatView';
import HealthcareChat from './features/healthcareChat/HealthcareChat';
import AskRedditPersonasChat from './features/digitalTwin/AskRedditPersonasChat';
import MaintenanceMode from './components/MaintenanceMode';
import AudienceDefine from './features/buildAudience/AudienceDefine';
import AudienceSegmentsFocus from './features/buildAudience/AudienceSegmentsFocus';
import AudienceSegmentationApproach from './features/buildAudience/AudienceSegmentationApproach';
import AudienceFinalize from './features/buildAudience/AudienceFinalize';
import PricingPage from './features/subscription/PricingPage';
import SubscriptionSuccess from './features/subscription/SubscriptionSuccess';
import SubscriptionCancelled from './features/subscription/SubscriptionCancelled';
import { checkResetAuthorization } from './services/adminService';
import SurveyBuilder from './features/aiSurvey/SurveyBuilder';
import SurveysList from './features/aiSurvey/SurveysList';
import SurveySession from './features/aiSurvey/SurveySession';
import SurveyDetails from './features/aiSurvey/SurveyDetails';
import SurveyAnalysisPage from './features/aiSurvey/SurveyAnalysisPage';
import SurveyComplete from './features/aiSurvey/SurveyComplete';
import MobilePhotoUpload from './features/aiSurvey/MobilePhotoUpload';
import SurveyResponses from './features/aiSurvey/SurveyResponses';
import SessionResponses from './features/aiSurvey/SessionResponses';
import SurveyGroupDetails from './features/aiSurvey/SurveyGroupDetails';
import UnifiedSegmentsSelector from './components/segments/UnifiedSegmentsSelector';
import { PrivacyPolicy, TermsOfService, AcceptableUsePolicy } from './features/legal';

type Step =
  | 'dashboard'
  | 'use-case-form'
  | 'select-audience'
  | 'select-segments'
  | 'review-and-run'
  | 'simulation-results'
  | 'audience-define'
  | 'audience-questionnaire'
  | 'audience-segments'
  | 'audience-final';

export interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { updateAudienceData } = useAudience();
  const { currentStep: buildCurrentStep, setCurrentStep: setBuildCurrentStep, resetBuildAudienceData } = useBuildAudience();

  const [stepMapping, setStepMapping] = useState<Step>('dashboard');
  const [flowType, setFlowType] = useState<'build' | 'simulate' | null>(null);

  // New flow state
  const [selectedUseCaseType, setSelectedUseCaseType] = useState<UseCaseType>(null);
  const [selectedUseCaseTitle, setSelectedUseCaseTitle] = useState<string>('');
  const [savedFormPayload, setSavedFormPayload] = useState<Record<string, any> | null>(null);
  const [selectedAudienceId, setSelectedAudienceId] = useState<number | null>(null);
  const [selectedAudienceName, setSelectedAudienceName] = useState<string>('');
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [selectedSegmentNames, setSelectedSegmentNames] = useState<string[]>([]);
  const [personaFilters, setPersonaFilters] = useState<Record<number, SegmentPersonaFilters>>({});
  const [currentSimulationId, setCurrentSimulationId] = useState<number | null>(null);
  const [formState, setFormState] = useState<Record<string, any>>({});
  const [visitedSteps, setVisitedSteps] = useState<Set<Step>>(new Set(['dashboard']));

  const { identifyUser, setTag } = useClarityTracking();

  useEffect(() => {
    if (isAuthenticated && user) {
      identifyUser(user.id.toString(), user.email);
      setTag('user_type', user.is_admin ? 'admin' : 'regular');
      setTag('auth_status', 'authenticated');
    } else {
      setTag('auth_status', 'unauthenticated');
    }
  }, [isAuthenticated, user, identifyUser, setTag]);

  // Build audience step mapping (unchanged)
  const updateStepMapping = useCallback((step: number) => {
    switch (step) {
      case 101: setStepMapping('audience-define'); break;
      case 102: setStepMapping('audience-questionnaire'); break;
      case 103: setStepMapping('audience-segments'); break;
      case 104: setStepMapping('audience-final'); break;
      default: setStepMapping('dashboard');
    }
  }, []);

  const handleNext = () => {
    const nextStep = buildCurrentStep + 1;
    setBuildCurrentStep(nextStep);
    updateStepMapping(nextStep);
  };

  const handleBack = () => {
    if (buildCurrentStep > 101) {
      const prevStep = buildCurrentStep - 1;
      setBuildCurrentStep(prevStep);
      updateStepMapping(prevStep);
    } else {
      setStepMapping('dashboard');
      setFlowType(null);
    }
  };

  const resetAll = () => {
    setStepMapping('dashboard');
    setBuildCurrentStep(101);
    setFlowType(null);
    setSelectedUseCaseType(null);
    setSelectedUseCaseTitle('');
    setSavedFormPayload(null);
    setSelectedAudienceId(null);
    setSelectedAudienceName('');
    setSelectedSegmentIds([]);
    setSelectedSegmentNames([]);
    setPersonaFilters({});
    setCurrentSimulationId(null);
    setFormState({});
    setVisitedSteps(new Set(['dashboard']));
    updateAudienceData({
      type: null, websiteUrl: '', segmentType: null, specificSegment: '',
      qualitativeInfo: '', uploadedFile: null, audienceId: null, audienceName: '',
      selectedUseCase: null, selectedSegments: [], personaFilters: {},
    });
    resetBuildAudienceData();
  };

  // Route-based step initialization
  useEffect(() => {
    if (location.pathname === '/build-audience') {
      setFlowType('build');
      setBuildCurrentStep(101);
      updateStepMapping(101);
    } else if (location.pathname === '/simulate') {
      setFlowType('simulate');
      if (stepMapping === 'dashboard') {
        // Don't reset if we're in the middle of a flow
      }
    } else if (location.pathname === '/') {
      resetAll();
    } else if (
      location.pathname.startsWith('/analysis') ||
      location.pathname === '/chat-with-persona' ||
      location.pathname === '/chat' ||
      location.pathname === '/admin' ||
      location.pathname.startsWith('/subscription')
    ) {
      setStepMapping('dashboard');
      setFlowType(null);
    }
  }, [location.pathname]);

  // --- New flow handlers ---

  const handleSelectUseCase = (useCaseType: UseCaseType, useCaseTitle: string) => {
    setSelectedUseCaseType(useCaseType);
    setSelectedUseCaseTitle(useCaseTitle);
    setSavedFormPayload(null);
    setSelectedAudienceId(null);
    setSelectedAudienceName('');
    setSelectedSegmentIds([]);
    setSelectedSegmentNames([]);
    setPersonaFilters({});
    setCurrentSimulationId(null);
    setFlowType('simulate');
    setStepMapping('use-case-form');
    updateAudienceData({ selectedUseCase: useCaseTitle, selectedSegments: [], personaFilters: {} });
    setVisitedSteps(new Set(['dashboard', 'use-case-form']));
    navigate('/simulate');
  };

  const handleFormSavePayload = (payload: Record<string, any>) => {
    setSavedFormPayload(payload);

    if (selectedUseCaseType === 'driving-concept-test' || selectedUseCaseType === 'vitamin-concept-test') {
      setStepMapping('review-and-run');
      setVisitedSteps(prev => new Set([...prev, 'select-audience', 'select-segments', 'review-and-run']));
      return;
    }

    setStepMapping('select-audience');
    setVisitedSteps(prev => new Set([...prev, 'select-audience']));
  };

  const handleFormDataChange = (useCaseType: string, data: Record<string, any>) => {
    const key = useCaseType.toLowerCase();
    if (JSON.stringify(formState[key]) === JSON.stringify(data)) return;
    setFormState(prev => ({ ...prev, [key]: data }));
  };

  const handleSelectAudience = (audienceId: number, audienceName: string) => {
    setSelectedAudienceId(audienceId);
    setSelectedAudienceName(audienceName);
    updateAudienceData({
      audienceId, audienceName,
      selectedSegments: [], personaFilters: {},
    });
    setStepMapping('select-segments');
    setVisitedSteps(prev => new Set([...prev, 'select-segments']));
  };

  const handleSegmentSelect = (segments: number[], filters: Record<number, SegmentPersonaFilters>) => {
    setSelectedSegmentIds(segments);
    setPersonaFilters(filters);
    updateAudienceData({ selectedSegments: segments, personaFilters: filters });

    // Fetch segment names for the review page
    fetchSegmentNames(segments);

    setStepMapping('review-and-run');
    setVisitedSteps(prev => new Set([...prev, 'review-and-run']));
  };

  const fetchSegmentNames = async (segmentIds: number[]) => {
    if (!selectedAudienceId || segmentIds.length === 0) return;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/audiences/${selectedAudienceId}/segments`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const segments = Array.isArray(data) ? data : data.segments || [];
        const names = segments
          .filter((s: any) => segmentIds.includes(s.id))
          .map((s: any) => s.name);
        setSelectedSegmentNames(names);
      }
    } catch {
      setSelectedSegmentNames(segmentIds.map(id => `Segment ${id}`));
    }
  };

  const handleSimulationComplete = (simulationId: number) => {
    setCurrentSimulationId(simulationId);
    setStepMapping('simulation-results');
    setVisitedSteps(prev => new Set([...prev, 'simulation-results']));
  };

  const handleEditFromReview = (targetStep: Step) => {
    const stepOrder: Step[] = ['dashboard', 'use-case-form', 'select-audience', 'select-segments', 'review-and-run'];
    const targetIdx = stepOrder.indexOf(targetStep);

    if (targetIdx >= 0) {
      const stepsToKeep = new Set(stepOrder.slice(0, targetIdx + 1));
      setVisitedSteps(prev => {
        const next = new Set<Step>();
        prev.forEach(s => { if (stepsToKeep.has(s)) next.add(s); });
        return next;
      });
    }

    if (targetStep === 'dashboard') {
      setSelectedUseCaseType(null);
      setSelectedUseCaseTitle('');
      setSavedFormPayload(null);
      setSelectedAudienceId(null);
      setSelectedAudienceName('');
      setSelectedSegmentIds([]);
      setSelectedSegmentNames([]);
      setPersonaFilters({});
    } else if (targetStep === 'use-case-form') {
      setSelectedAudienceId(null);
      setSelectedAudienceName('');
      setSelectedSegmentIds([]);
      setSelectedSegmentNames([]);
      setPersonaFilters({});
    } else if (targetStep === 'select-audience') {
      setSelectedSegmentIds([]);
      setSelectedSegmentNames([]);
      setPersonaFilters({});
    }

    setStepMapping(targetStep);
  };

  // --- Progress steps ---

  const getProgressSteps = () => {
    if (flowType === 'build') {
      return [
        {
          label: '1. Define Target',
          completed: buildCurrentStep > 101,
          current: buildCurrentStep === 101,
          onClick: buildCurrentStep > 101 ? () => { setBuildCurrentStep(101); updateStepMapping(101); } : undefined,
        },
        {
          label: '2. Define Focus',
          completed: buildCurrentStep > 102,
          current: buildCurrentStep === 102,
          onClick: buildCurrentStep > 102 ? () => { setBuildCurrentStep(102); updateStepMapping(102); } : undefined,
        },
        {
          label: '3. Define Approach & Build',
          completed: buildCurrentStep > 103,
          current: buildCurrentStep === 103 || buildCurrentStep === 104,
          onClick: buildCurrentStep > 103 ? () => { setBuildCurrentStep(103); updateStepMapping(103); } : undefined,
        },
      ];
    }

    if (flowType === 'simulate') {
      const isResults = stepMapping === 'simulation-results';
      const isDriving = selectedUseCaseType === 'driving-concept-test';
      const isVitamin = selectedUseCaseType === 'vitamin-concept-test';
      const isExternalConceptTest = isDriving || isVitamin;

      if (isExternalConceptTest) {
        const stepLabel = isDriving ? '1. Driving Concept Test' : '1. Concept Test';
        return [
          {
            label: stepLabel,
            completed: visitedSteps.has('use-case-form') || isResults,
            current: stepMapping === 'dashboard',
            onClick: () => { setStepMapping('dashboard'); },
          },
          {
            label: '2. Configure',
            completed: visitedSteps.has('review-and-run') || isResults,
            current: stepMapping === 'use-case-form',
            onClick: visitedSteps.has('use-case-form') ? () => { setStepMapping('use-case-form'); } : undefined,
          },
          {
            label: '3. Review & Run',
            completed: isResults,
            current: stepMapping === 'review-and-run',
            onClick: visitedSteps.has('review-and-run') ? () => { setStepMapping('review-and-run'); } : undefined,
          },
        ];
      }

      return [
        {
          label: selectedUseCaseTitle ? `1. ${selectedUseCaseTitle}` : '1. Select Use Case',
          completed: visitedSteps.has('use-case-form') || isResults,
          current: stepMapping === 'dashboard',
          onClick: () => { setStepMapping('dashboard'); },
        },
        {
          label: '2. Complete Form',
          completed: visitedSteps.has('select-audience') || isResults,
          current: stepMapping === 'use-case-form',
          onClick: visitedSteps.has('use-case-form') ? () => { setStepMapping('use-case-form'); } : undefined,
        },
        {
          label: selectedAudienceName ? `3. ${selectedAudienceName}` : '3. Select Audience',
          completed: visitedSteps.has('select-segments') || isResults,
          current: stepMapping === 'select-audience',
          onClick: visitedSteps.has('select-audience') ? () => { setStepMapping('select-audience'); } : undefined,
        },
        {
          label: '4. Select Segments',
          completed: visitedSteps.has('review-and-run') || isResults,
          current: stepMapping === 'select-segments',
          onClick: visitedSteps.has('select-segments') ? () => { setStepMapping('select-segments'); } : undefined,
        },
        {
          label: '5. Review & Run',
          completed: isResults,
          current: stepMapping === 'review-and-run',
          onClick: visitedSteps.has('review-and-run') ? () => { setStepMapping('review-and-run'); } : undefined,
        },
      ];
    }

    return [];
  };

  // --- Render ---

  const renderCurrentStep = () => {
    switch (stepMapping) {
      case 'dashboard':
        return <LandingScreen onSelectUseCase={handleSelectUseCase} />;

      case 'use-case-form':
        return (
          <UseCaseFormRenderer
            useCaseType={selectedUseCaseType}
            onSavePayload={handleFormSavePayload}
            onBack={() => setStepMapping('dashboard')}
            onFormDataChange={handleFormDataChange}
            initialFormData={formState[selectedUseCaseType as string] || {}}
            selectedSegmentIds={selectedSegmentIds}
            personaFilters={personaFilters}
          />
        );

      case 'select-audience':
        return (
          <ExistingAudiences
            onSelectAudience={handleSelectAudience}
            onBack={() => setStepMapping('use-case-form')}
          />
        );

      case 'select-segments':
        return (
          <UnifiedSegmentsSelector
            audienceId={selectedAudienceId || 0}
            btnText="Continue to Review"
            onBack={() => setStepMapping('select-audience')}
            onNext={handleSegmentSelect}
          />
        );

      case 'review-and-run':
        return (
          <ReviewAndRun
            useCaseType={selectedUseCaseType as string}
            useCaseTitle={selectedUseCaseTitle}
            audienceName={selectedAudienceName}
            audienceId={selectedAudienceId || 0}
            segmentNames={selectedSegmentNames}
            selectedSegmentIds={selectedSegmentIds}
            personaFilters={personaFilters}
            formPayload={savedFormPayload || {}}
            onEdit={handleEditFromReview}
            onComplete={handleSimulationComplete}
          />
        );

      case 'simulation-results':
        return (
          <div className="w-full" style={{ height: '90vh', overflow: 'hidden' }}>
            {currentSimulationId ? (
              <div className="h-full flex flex-col">
                <div className="mb-4 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setStepMapping('review-and-run')}
                    icon={<ArrowLeft className="w-4 h-4 mr-1" />}
                  >
                    Back to review
                  </Button>
                </div>
                <div className="flex-grow overflow-hidden">
                  <SimulationResultsContent
                    simulationId={currentSimulationId}
                    defaultChatOpen={true}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No simulation results to display.</p>
                <Button variant="outline" className="mt-4" onClick={resetAll}>
                  Start a new study
                </Button>
              </div>
            )}
          </div>
        );

      // Build audience flow (unchanged)
      case 'audience-define':
        return <AudienceDefine onNext={handleNext} onBack={handleBack} />;
      case 'audience-questionnaire':
        return <AudienceSegmentsFocus onNext={handleNext} onBack={handleBack} />;
      case 'audience-segments':
        return <AudienceSegmentationApproach onNext={handleNext} onBack={handleBack} />;
      case 'audience-final':
        return <AudienceFinalize onNext={resetAll} onBack={handleBack} />;

      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return <Auth onAuthSuccess={() => {}} />;
  }

  return (
    <Layout>
      {stepMapping !== 'dashboard' && (
        <div className="mb-4">
          <ProgressSteps steps={getProgressSteps()} />
        </div>
      )}
      {renderCurrentStep()}
    </Layout>
  );
};

// Authentication component with navigation
const AuthRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (isAuthenticated) {
    const state = location.state as { from?: { pathname: string } };
    // Check redirect query param first, then location state, then default to home
    const redirectParam = searchParams.get('redirect');
    const from = redirectParam || state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return <Auth onAuthSuccess={() => {}} />;
};

// Wrapper for SimulationResultsContent to use in routing
const SimulationResultsWrapper: React.FC = () => {
  const { simulationId } = useParams<{ simulationId: string }>();

  const handleError = (error: string) => {
    console.error('Simulation error:', error);
  };

  return (
    <Layout>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden h-full">
        <SimulationResultsContent
          simulationId={simulationId || ''}
          onError={handleError}
          defaultChatOpen={true}
        />
      </div>
    </Layout>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
      <Route path="/build-audience" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
      <Route path="/simulate" element={<ProtectedRoute><AppContent /></ProtectedRoute>} />
      <Route path="/simulation-results/:simulationId" element={<ProtectedRoute><SimulationResultsWrapper /></ProtectedRoute>} />
      <Route path="/analysis" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
      <Route path="/analysis/:simulationId" element={<ProtectedRoute><AnalysisPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/chat-with-persona" element={<ProtectedRoute><ChatWithPersona /></ProtectedRoute>} />
      <Route path="/ask-collective" element={<AskCollectiveLanding />} />
      <Route path="/ask-collective/chat" element={<AskCollectiveChat />} />
      <Route path="/ask-healthcare" element={<ProtectedRoute><AskHealthcareChat /></ProtectedRoute>} />
      <Route path="/healthcare-chat" element={<ProtectedRoute><HealthcareChat /></ProtectedRoute>} />
      <Route path="/ask-natural-organic/:sessionId?" element={<ProtectedRoute><AskNaturalOrganicChat /></ProtectedRoute>} />
      <Route path="/chat/shared/:shareToken" element={<SharedChatView />} />
      <Route path="/ask-reddit-personas" element={<ProtectedRoute><AskRedditPersonasChat /></ProtectedRoute>} />
      <Route path="/subscription/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/subscription/cancelled" element={<ProtectedRoute><SubscriptionCancelled /></ProtectedRoute>} />
      <Route path="/surveys" element={<ProtectedRoute><SurveysList /></ProtectedRoute>} />
      <Route path="/surveys/new" element={<ProtectedRoute><SurveyBuilder /></ProtectedRoute>} />
      <Route path="/surveys/:id" element={<ProtectedRoute><SurveyDetails /></ProtectedRoute>} />
      <Route path="/surveys/:shareId/take" element={<SurveySession />} />
      <Route path="/survey-groups/:groupId" element={<ProtectedRoute><SurveyGroupDetails /></ProtectedRoute>} />
      <Route path="/simulation/public/:shareToken" element={<PublicSimulationView />} />
      <Route path="/surveys/:id/analysis" element={<ProtectedRoute><SurveyAnalysisPage /></ProtectedRoute>} />
      <Route path="/surveys/:id/responses" element={<ProtectedRoute><SurveyResponses /></ProtectedRoute>} />
      <Route path="/surveys/sessions/:sessionId/responses" element={<ProtectedRoute><SessionResponses /></ProtectedRoute>} />
      <Route path="/survey-complete/:sessionId" element={<SurveyComplete />} />
      <Route path="/upload/:uploadToken" element={<MobilePhotoUpload />} />
      <Route path="/auth" element={<AuthRoute />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
    </Routes>
  );
};

function App() {
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    const clarityProjectId = import.meta.env.VITE_CLARITY_PROJECT_ID;
    const isDevelopment = import.meta.env.DEV;
    if (clarityProjectId && (!isDevelopment || import.meta.env.VITE_CLARITY_ENABLE_DEV === 'true')) {
      try {
        Clarity.init(clarityProjectId);
      } catch (error) {
        console.warn('Failed to initialize Microsoft Clarity:', error);
      }
    }
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <SubscriptionProvider>
          <AudienceProvider>
            <AudienceDataProvider>
              <BuildAudienceProvider>
                <Router>
                  {maintenanceMode ? <MaintenanceWrapper /> : <AppRoutes />}
                </Router>
              </BuildAudienceProvider>
            </AudienceDataProvider>
          </AudienceProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

const MaintenanceWrapper: React.FC = () => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const maintenanceMode = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

  useEffect(() => {
    const checkAuth = async () => {
      const authorized = await checkResetAuthorization();
      setIsAuthorized(authorized);
    };
    checkAuth();
  }, []);

  if (isAuthorized === null) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (maintenanceMode && !isAuthorized) {
    return <MaintenanceMode />;
  }

  return <AppRoutes />;
};

export default App;
